import { useState } from 'react';
import {
  Box,
  Select,
  MenuItem,
  TextField,
  Button,
  SelectChangeEvent,
} from '@mui/material';
import { CalendarMonth, Check } from '@mui/icons-material';

export type DateFilterValue = 'today' | 'yesterday' | 'last_7_days' | 'last_30_days' | 'this_month' | 'last_month' | 'this_year' | 'custom' | 'all';

interface DateFilterProps {
  value: DateFilterValue;
  onChange: (filter: DateFilterValue, startDate?: string, endDate?: string) => void;
  startDate?: string;
  endDate?: string;
}

const dateRanges: { value: DateFilterValue; label: string }[] = [
  { value: 'all', label: 'All Time' },
  { value: 'today', label: 'Today' },
  { value: 'yesterday', label: 'Yesterday' },
  { value: 'last_7_days', label: 'Last 7 Days' },
  { value: 'last_30_days', label: 'Last 30 Days' },
  { value: 'this_month', label: 'This Month' },
  { value: 'last_month', label: 'Last Month' },
  { value: 'this_year', label: 'This Year' },
  { value: 'custom', label: 'Custom Range' },
];

export const DateFilter = ({ value, onChange, startDate, endDate }: DateFilterProps) => {
  const [localStartDate, setLocalStartDate] = useState(startDate || '');
  const [localEndDate, setLocalEndDate] = useState(endDate || '');

  const handleSelectChange = (event: SelectChangeEvent<DateFilterValue>) => {
    const newValue = event.target.value as DateFilterValue;

    if (newValue !== 'custom') {
      onChange(newValue);
    } else {
      // Just update the UI to show custom inputs
      onChange(newValue, localStartDate, localEndDate);
    }
  };

  const handleCustomSubmit = () => {
    if (localStartDate && localEndDate) {
      onChange('custom', localStartDate, localEndDate);
    }
  };

  return (
    <Box
      sx={{
        display: 'flex',
        alignItems: 'center',
        gap: 1,
        bgcolor: 'white',
        borderRadius: '24px',
        p: 0.5,
        boxShadow: '0 2px 8px rgba(0,0,0,0.08)',
      }}
    >
      <CalendarMonth sx={{ ml: 1.5, color: 'text.secondary', fontSize: 20 }} />

      <Select
        value={value}
        onChange={handleSelectChange}
        variant="standard"
        disableUnderline
        sx={{
          minWidth: 150,
          fontWeight: 600,
          fontSize: '0.875rem',
          '& .MuiSelect-select': {
            py: 0.5,
            pr: 4,
          },
        }}
      >
        {dateRanges.map((range) => (
          <MenuItem key={range.value} value={range.value}>
            {range.label}
          </MenuItem>
        ))}
      </Select>

      {value === 'custom' && (
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            gap: 1,
            pl: 2,
            borderLeft: '1px solid',
            borderColor: 'grey.200',
          }}
        >
          <TextField
            type="date"
            value={localStartDate}
            onChange={(e) => setLocalStartDate(e.target.value)}
            variant="standard"
            InputProps={{ disableUnderline: true }}
            sx={{
              width: 130,
              '& input': {
                fontSize: '0.875rem',
                py: 0.5,
              },
            }}
          />
          <Box sx={{ color: 'text.secondary' }}>-</Box>
          <TextField
            type="date"
            value={localEndDate}
            onChange={(e) => setLocalEndDate(e.target.value)}
            variant="standard"
            InputProps={{ disableUnderline: true }}
            sx={{
              width: 130,
              '& input': {
                fontSize: '0.875rem',
                py: 0.5,
              },
            }}
          />
          <Button
            onClick={handleCustomSubmit}
            variant="contained"
            size="small"
            sx={{
              minWidth: 'auto',
              width: 32,
              height: 32,
              p: 0,
              borderRadius: '50%',
              mr: 0.5,
            }}
          >
            <Check sx={{ fontSize: 18 }} />
          </Button>
        </Box>
      )}
    </Box>
  );
};
